import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAccount, useSignMessage } from 'wagmi';
import bs58 from 'bs58';

import { api, type OwnerProfile } from '../lib/api';

type Chain = 'solana' | 'ethereum';

interface SessionValue {
  profile: OwnerProfile | null;
  loading: boolean;
  /** The chain of the wallet currently signed in, if any. */
  chain: Chain | null;
  address: string | null;
  signIn: (chain: Chain) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  error: string | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<OwnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const solana = useWallet();
  const evm = useAccount();
  const { signMessageAsync } = useSignMessage();

  const refresh = useCallback(async () => {
    try {
      setProfile(await api.me());
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Wallet sign-in. The nonce comes from the server and is echoed inside the
   * signed statement, so a signature captured from one session cannot be
   * replayed into another.
   */
  const signIn = useCallback(
    async (chain: Chain) => {
      setError(null);
      try {
        if (chain === 'solana') {
          if (!solana.publicKey || !solana.signMessage) {
            throw new Error('connect a Solana wallet first');
          }
          const address = solana.publicKey.toBase58();
          const { message } = await api.nonce('solana', address);
          const signature = await solana.signMessage(new TextEncoder().encode(message));
          await api.verify({
            chain: 'solana',
            address,
            message,
            signature: bs58.encode(signature),
          });
        } else {
          if (!evm.address) throw new Error('connect an Ethereum wallet first');
          const { message } = await api.nonce('ethereum', evm.address);
          const signature = await signMessageAsync({ message });
          await api.verify({ chain: 'ethereum', address: evm.address, message, signature });
        }
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'sign-in failed');
        throw err;
      }
    },
    [solana, evm.address, signMessageAsync, refresh],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    setProfile(null);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      profile,
      loading,
      chain: (profile?.chain as Chain) ?? null,
      address: profile?.address ?? null,
      signIn,
      signOut,
      refresh,
      error,
    }),
    [profile, loading, signIn, signOut, refresh, error],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
